'use client'
import React, { useState, useId } from 'react'
import parse from 'html-react-parser'

const RTL_LANGS = new Set(['he', 'ar'])

// ─── Multilingual defaults ─────────────────────────────────────────────────────
const LABELS = {
  readMore: { en: 'Read more', th: 'อ่านเพิ่มเติม', cn: '阅读更多', ru: 'Читать далее', de: 'Mehr lesen', it: 'Leggi di più', fr: 'Lire la suite', es: 'Leer más', ja: 'もっと読む', ko: '더 읽기', tr: 'Devamını oku', he: 'קרא עוד', ar: 'اقرأ المزيد' },
  readLess: { en: 'Read less', th: 'ย่อข้อความ',    cn: '收起',     ru: 'Свернуть',    de: 'Weniger',     it: 'Leggi meno',  fr: 'Réduire',       es: 'Leer menos', ja: '閉じる',   ko: '접기',   tr: 'Daha az',      he: 'קרא פחות', ar: 'اقرأ أقل'  },
}

const LISTING_DETAILS = {
  en: 'Listing Details', th: 'รายละเอียด', cn: '房源详情', ru: 'Детали объявления',
  de: 'Objektdetails', it: 'Dettagli annuncio', fr: 'Détails de l\'annonce',
  es: 'Detalles del anuncio', ja: '物件詳細', ko: '매물 상세정보', tr: 'İlan Detayları',
  he: 'פרטי הנכס', ar: 'تفاصيل الإعلان',
}

// ─── Button style renderer ─────────────────────────────────────────────────────
function ReadMoreButton({ label, btnStyle, color, textColor, fontSize, fontWeight, paddingX, paddingY, borderRadius, borderWidth, onClick }) {
  const px = `${paddingY ?? 4}px ${paddingX ?? 16}px`
  const radius = btnStyle === 'pill' ? 999 : (borderRadius ?? 6)
  const base = { cursor: 'pointer', marginTop: 8, fontSize: fontSize ?? '0.875rem', fontWeight: fontWeight ?? '400', background: 'none', border: 'none', padding: 0, lineHeight: 1.4 }

  if (btnStyle === 'filled') return (
    <button onClick={onClick} style={{ ...base, background: color, color: textColor ?? '#fff', padding: px, borderRadius: radius }}>
      {label}
    </button>
  )
  if (btnStyle === 'outlined' || btnStyle === 'pill') return (
    <button onClick={onClick} style={{ ...base, color, border: `${borderWidth ?? 1}px solid ${color}`, padding: px, borderRadius: radius }}>
      {label}
    </button>
  )
  return (
    <button onClick={onClick} style={{ ...base, color, textDecoration: 'underline', textUnderlineOffset: 2 }}>
      {label}
    </button>
  )
}

// ─── Responsive style injector ─────────────────────────────────────────────────
function ResponsiveStyle({ uid, titleSize, contentColor }) {
  const rules = []

  if (titleSize && typeof titleSize === 'object') {
    if (titleSize.pc)     rules.push(`.desc-title-${uid} { font-size: ${titleSize.pc}; }`)
    if (titleSize.mobile) rules.push(`@media (max-width: 1023px) { .desc-title-${uid} { font-size: ${titleSize.mobile}; } }`)
  }

  if (contentColor) {
    rules.push(`.desc-content-${uid} { color: ${contentColor}; }`)
    rules.push(`.desc-content-${uid} p, .desc-content-${uid} li, .desc-content-${uid} span:not([style*="color"]) { color: ${contentColor}; }`)
  }

  if (!rules.length) return null
  return <style dangerouslySetInnerHTML={{ __html: rules.join('\n') }} />
}

// ─── Main component ────────────────────────────────────────────────────────────
const HtmlComponent = ({ subcategory, html, config = {}, lang = 'en' }) => {
  const uid   = useId().replace(/:/g, '')
  const isRTL = RTL_LANGS.has(lang)

  const {
    collapseMode        = 'collapsed',
    collapseHeight      = 120,
    titleColor          = 'var(--color-main)',
    titleFw             = '600',
    titleSize           = '1.25rem',
    titleAlign          = 'left',
    titleLabels         = {},
    showTitle           = true,
    contentColor        = 'var(--color-content)',
    btnStyle            = 'outlined',
    btnColor            = 'var(--color-main)',
    btnTextColor        = '#ffffff',
    btnAlign            = 'right',
    btnFontSize         = '0.875rem',
    btnFontWeight       = '400',
    btnPaddingX         = '16',
    btnPaddingY         = '4',
    btnBorderRadius     = '6',
    btnBorderWidth      = '1',
    readMoreLabels      = {},
    readLessLabels      = {},
  } = config

  const resolve = (obj, fallback) => obj?.[lang]?.trim() || obj?.en?.trim() || fallback

  const resolvedTitle = resolve(titleLabels, LISTING_DETAILS[lang] ?? LISTING_DETAILS.en)
  const resolvedReadMore = resolve(readMoreLabels, LABELS.readMore[lang] ?? LABELS.readMore.en)
  const resolvedReadLess = resolve(readLessLabels, LABELS.readLess[lang] ?? LABELS.readLess.en)

  const [open, setOpen] = useState(collapseMode !== 'collapsed')

  if (html?.[0]?.trim()?.length === 0) return null

  const maxContentLength = 200
  const isContentTruncated = html?.[0]?.length > maxContentLength

  // For RTL, flip btn alignment: left→right, right→left
  const effectiveBtnAlign = isRTL
    ? (btnAlign === 'left' ? 'right' : btnAlign === 'right' ? 'left' : btnAlign)
    : btnAlign
  const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' }
  const btnJustify = justifyMap[effectiveBtnAlign] ?? 'flex-end'

  const isTitleSizeResponsive = titleSize && typeof titleSize === 'object'
  const titleFontSizeInline   = isTitleSizeResponsive ? undefined : (titleSize ?? '1.25rem')

  const titleStyle = {
    color:        titleColor,
    fontWeight:   titleFw,
    fontSize:     titleFontSizeInline,
    textAlign:    isRTL ? 'right' : titleAlign,
    margin:       0,
    marginBottom: 6,
  }

  return (
    <div className="mb-3 pb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <ResponsiveStyle
        uid={uid}
        titleSize={isTitleSizeResponsive ? titleSize : null}
        contentColor={contentColor}
      />

      {showTitle && resolvedTitle && (
        <h2
          className={isTitleSizeResponsive ? `desc-title-${uid}` : undefined}
          style={titleStyle}
        >
          {resolvedTitle}
        </h2>
      )}

      <div>
        <div
          className={`desc-content-${uid}`}
          style={(!open && collapseMode !== 'none') ? { height: collapseHeight, overflow: 'hidden' } : undefined}
        >
          {parse(`${html}`)}
        </div>

        {collapseMode !== 'none' && isContentTruncated && (
          <div style={{ position: 'relative' }}>
            {!open && (
              <div style={{ position: 'absolute', top: -20, left: 0, right: 0, height: 20, background: 'linear-gradient(to bottom, transparent, var(--color-light, #fff))' }} />
            )}
            <div style={{ display: 'flex', justifyContent: btnJustify, marginTop: open ? 4 : 0 }}>
              <ReadMoreButton
                label={open ? resolvedReadLess : resolvedReadMore}
                btnStyle={btnStyle}
                color={btnColor}
                textColor={btnTextColor}
                fontSize={btnFontSize}
                fontWeight={btnFontWeight}
                paddingX={Number(btnPaddingX)}
                paddingY={Number(btnPaddingY)}
                borderRadius={Number(btnBorderRadius)}
                borderWidth={Number(btnBorderWidth)}
                onClick={() => setOpen(o => !o)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HtmlComponent